import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'motion/react';
import { ArrowRight, Star, CheckCircle2, Trophy, Target, Award, Camera, Users, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PROGRAMS, WINNERS } from '../data';
import { Winner } from '../types';
import Modal from '../components/Modal';
import Seo from '../components/Seo';
import { PAGE_METADATA, getHomeStructuredData } from '../seo/pageMetadata';

const ABOUT_IMAGES = [
  {
    src: 'https://i.ibb.co/7drkXzKX/Delight-3rd-Place-Miss-Gloria.jpg',
    alt: 'Delight Business Empowerment 3rd place winner Miss Gloria portrait',
  },
  {
    src: 'https://i.ibb.co/dwLfXR8p/Delight-2nd-Place-Miss-Glory-500-K.jpg',
    alt: 'Delight Business Empowerment 2nd place winner Miss Glory portrait',
  },
  {
    src: 'https://i.ibb.co/7xsHJQ0q/Delight-1st-Place-Miss-Damilola-1-M.jpg',
    alt: 'Delight Business Empowerment 1st place winner Miss Damilola portrait',
  },
  {
    src: 'https://i.ibb.co/Xr1XKQwq/Delight-1st-Place-Miss-Daniela-2-M-1.jpg',
    alt: 'Delight Business Empowerment 1st place winner Miss Daniela portrait variation one',
  },
  {
    src: 'https://i.ibb.co/FkcWM976/Delight-1st-Place-Miss-Daniela-2-M.jpg',
    alt: 'Delight Business Empowerment 1st place winner Miss Daniela portrait variation two',
  },
  {
    src: 'https://i.ibb.co/Hp4q8PyB/1.jpg',
    alt: 'Delight Business Empowerment 1st Place winner Miss Bassey portrait',
  },
  {
    src: 'https://i.ibb.co/zTcvY9sz/Delight-2nd-Place-Miss-Olamide-1-M.jpg',
    alt: 'Delight Business Empowerment 2nd place winner Miss Olamide portrait',
  },
  {
    src: 'https://i.ibb.co/MHFhwV8/Delight-3rd-Place-Miss-Gloria-500-K.jpg',
    alt: 'Delight Business Empowerment 3rd place winner Miss Gloria portrait variation two',
  },
  {
    src: 'https://i.ibb.co/xS3C0XRF/1.jpg',
    alt: 'Delight Business Empowerment 2nd Place winner Miss Fatima portrait',
  },
];

const Home = () => {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, 200]);
  const y2 = useTransform(scrollY, [0, 500], [0, -150]);
  const [selectedWinner, setSelectedWinner] = useState<Winner | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % ABOUT_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <Seo {...PAGE_METADATA.home} structuredData={getHomeStructuredData()} />
      <div className="pt-20">
        {/* Hero */}
        <section className="relative pt-20 pb-32 px-6 overflow-hidden text-center">
        {/* Background Image with Light Overlay */}
        <div className="absolute inset-0 -z-10">
          <img 
            src="https://images.unsplash.com/photo-1556761175-b413da4baf72?q=80&w=2000" 
            alt="Women collaborating during a Delight Business Empowerment session" 
            width={2000}
            height={1333}
            className="w-full h-full object-cover opacity-10"
            referrerPolicy="no-referrer"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-transparent to-white" />
        </div>

        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full -z-10 pointer-events-none">
          <motion.div style={{ y: y1 }} className="absolute top-20 right-[10%] w-96 h-96 bg-amber-100/60 rounded-full blur-3xl" />
          <motion.div style={{ y: y2 }} className="absolute bottom-20 left-[5%] w-80 h-80 bg-amber-50/50 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
<div className="inline-flex items-center gap-2 bg-royal-accent/50 border border-amber-200 px-4 py-2 rounded-full mb-8">
               <Award className="w-4 h-4 text-royal-purple fill-royal-purple" />
               <span className="text-royal-purple text-[10px] font-black uppercase tracking-[0.2em]">Delight Business Empowerment</span>
             </div>
            <h1 className="text-4xl sm:text-5xl md:text-8xl font-display font-black text-royal-dark leading-[0.95] tracking-tighter mb-8 break-words">
              Beauty with <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-royal-purple via-royal-purple to-royal-gold">Purpose & Impact</span>
            </h1>
<p className="mt-8 text-lg md:text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed font-medium">
               Delight Business Empowerment is more than a beauty competition; it's a platform for visionary women to showcase their intelligence, creativity, and drive for social change.
             </p>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center items-center px-4">
              <Link to="/faces" className="w-full sm:w-auto bg-royal-dark text-white px-6 sm:px-10 py-5 rounded-2xl font-black text-base sm:text-lg shadow-2xl flex items-center justify-center gap-3 group hover:bg-royal-purple transition-all">
                Meet Delight Business Empowerment <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/register" className="w-full sm:w-auto bg-white border-2 border-slate-100 text-slate-900 px-6 sm:px-10 py-5 rounded-2xl font-black text-base sm:text-lg hover:bg-slate-50 transition-all">
                Become Part of Delight Business Empowerment
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Us Preview Section */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Image Slider */}
          <div className="relative h-[500px] sm:h-[600px] rounded-[3rem] overflow-hidden shadow-2xl">
            <AnimatePresence mode="wait">
              <motion.img
                key={currentSlide}
                src={ABOUT_IMAGES[currentSlide].src}
                alt={ABOUT_IMAGES[currentSlide].alt}
                width={1200}
                height={1500}
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 1, ease: "easeInOut" }}
                className="absolute inset-0 w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-royal-dark/40 to-transparent" />
            
            {/* Slider Indicators */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-10">
              {ABOUT_IMAGES.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    currentSlide === idx ? "w-8 bg-white" : "w-2 bg-white/50"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Right: About Preview Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 bg-royal-accent/50 border border-amber-200 px-4 py-2 rounded-full">
              <Sparkles className="w-4 h-4 text-royal-purple" />
              <span className="text-royal-purple text-[10px] font-black uppercase tracking-[0.2em]">The Competition</span>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-display font-black text-royal-dark leading-tight tracking-tighter">
              Defining <span className="text-royal-purple">Beauty</span> <br />
              Through <span className="text-royal-gold">Empowerment</span>.
            </h2>
            
            <p className="text-xl text-slate-600 leading-relaxed font-medium">
              Delight Business Empowerment is a premier beauty competition that celebrates the modern woman. We believe that true beauty is found in the intersection of grace, intelligence, and a commitment to making a positive impact in the world.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-6 py-4">
              <div className="flex items-start gap-4">
                <div className="bg-royal-accent p-3 rounded-2xl">
                  <CheckCircle2 className="w-5 h-5 text-royal-purple" />
                </div>
                <div>
                  <h4 className="font-display font-black text-royal-dark">Proven Impact</h4>
                  <p className="text-sm text-slate-500 font-medium">Over 5,000 lives transformed through our initiatives.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-royal-accent p-3 rounded-2xl">
                  <CheckCircle2 className="w-5 h-5 text-royal-purple" />
                </div>
                <div>
                  <h4 className="font-display font-black text-royal-dark">Global Vision</h4>
                  <p className="text-sm text-slate-500 font-medium">Scaling local businesses to international standards.</p>
                </div>
              </div>
            </div>

            <Link 
              to="/about" 
              className="inline-flex items-center gap-3 bg-royal-dark text-white px-10 py-5 rounded-2xl font-black text-lg shadow-xl hover:bg-royal-purple transition-all group"
            >
              Learn More About Us
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Faces of Delight Section */}
      <section className="py-24 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <div className="max-w-xl">
              <span className="text-royal-gold font-black text-[10px] uppercase tracking-[0.3em] mb-4 block">Excellence in Action</span>
              <h2 className="text-4xl md:text-5xl font-display font-black text-royal-dark leading-tight break-words">
                Delight <span className="text-royal-purple">Business Empowerment</span>
              </h2>
              <p className="text-slate-500 mt-4 font-medium">
                Our goal is not only to celebrate winners, but to create lasting impact by building confidence, encouraging innovation, and promoting sustainable growth.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {WINNERS.slice(0, 3).map((winner, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                onClick={() => setSelectedWinner(winner)}
                className="group bg-white rounded-[2.5rem] overflow-hidden shadow-2xl shadow-black/5 cursor-pointer border border-slate-100/50"
              >
                <div className="h-[320px] overflow-hidden relative">
                  <img 
                    src={winner.img} 
                    alt={`Portrait of Miss ${winner.name}, ${winner.title} winner in ${winner.year}`}
                    width={1200}
                    height={1500}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" 
                    referrerPolicy="no-referrer"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-royal-dark/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                    <div className="bg-white/20 backdrop-blur-md p-3 rounded-full text-white scale-50 opacity-0 group-hover:scale-100 group-hover:opacity-100 transition-all duration-500">
                      <Camera className="w-6 h-6" />
                    </div>
                  </div>
                  <div className="absolute top-4 right-4 bg-white/95 backdrop-blur px-4 py-2 rounded-xl font-display font-black text-royal-purple text-[10px] shadow-xl">
                    {winner.prize}
                  </div>
                </div>
                <div className="p-8">
                  <span className="text-royal-gold font-black text-[8px] uppercase tracking-widest">{winner.title}</span>
                  <h4 className="text-xl font-display font-black text-royal-dark mt-1 uppercase tracking-tighter">Miss {winner.name}</h4>
                  <p className="text-royal-purple text-[10px] mt-3 font-black uppercase tracking-widest flex items-center gap-2 group-hover:gap-3 transition-all">
                    View More <ArrowRight className="w-3 h-3" />
                  </p>
                </div>
              </motion.div>
            ))}

            {/* Animated CTA Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <Link 
                to="/faces" 
                className="group relative h-full min-h-[400px] flex flex-col items-center justify-center text-center p-10 bg-royal-dark rounded-[2.5rem] overflow-hidden shadow-2xl hover:shadow-royal-purple/20 transition-all"
              >
                {/* Decorative background elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-royal-purple/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-700" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-royal-gold/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 group-hover:scale-150 transition-transform duration-700" />
                
                <div className="relative z-10 space-y-6">
                  <div className="w-20 h-20 bg-white/10 backdrop-blur-xl rounded-3xl flex items-center justify-center mx-auto group-hover:rotate-12 transition-transform duration-500">
                    <Users className="w-10 h-10 text-royal-gold" />
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="text-2xl font-display font-black text-white leading-tight">
                      Discover All <br />
                      <span className="text-royal-gold">Our Winners</span>
                    </h4>
                    <p className="text-white/50 text-sm font-medium">
                      Explore the full gallery of excellence and impact.
                    </p>
                  </div>

                  <div className="inline-flex items-center gap-2 bg-white text-royal-dark px-6 py-3 rounded-xl font-black text-sm group-hover:bg-royal-gold group-hover:text-royal-dark transition-colors">
                    View All Alumni
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Philosophy & Vision */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-12">
            <div>
              <h2 className="text-3xl md:text-5xl font-display font-black text-royal-dark mb-8 leading-tight">Our Philosophy</h2>
              <p className="text-xl text-slate-700 leading-relaxed font-medium">
                We believe that true empowerment goes beyond motivation — it involves providing real opportunities, practical support, valuable exposure, and the right resources to help people build a better future.
              </p>
            </div>
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-display font-black text-royal-purple uppercase tracking-widest mb-4">Our Vision</h3>
                <p className="text-slate-600 leading-relaxed text-lg font-medium">
                  Our platform was created with a clear vision: to uplift aspiring entrepreneurs, small business owners, and individuals with great potential by giving them access to empowerment initiatives that can transform their lives and businesses.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-display font-black text-royal-purple uppercase tracking-widest mb-4">Bridging the Gap</h3>
                <p className="text-slate-600 leading-relaxed text-lg font-medium">
                  We understand that many talented individuals and promising businesses struggle, not because they lack vision, but because they lack the support, visibility, and resources needed to move forward. That is why Delight Business Empowerment exists to bridge that gap and create a platform where effort, creativity, and determination can be recognized and rewarded.
                </p>
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-square bg-royal-accent rounded-[4rem] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=1000"
                alt="Mentorship and collaboration session for women entrepreneurs"
                width={1000}
                height={1000}
                className="w-full h-full object-cover mix-blend-overlay opacity-50"
                referrerPolicy="no-referrer"
              />
              <div className="absolute inset-0 flex items-center justify-center p-12">
                <div className="bg-white p-10 rounded-[3rem] shadow-2xl text-center">
                  <Target className="w-12 h-12 text-royal-purple mx-auto mb-4" />
                  <p className="font-display font-black text-royal-dark text-xl">Success is Nurtured</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact & Belief */}
      <section className="py-24 px-6 bg-royal-dark text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-display font-black mb-10">Building a Movement</h2>
          <p className="text-royal-accent/80 text-xl leading-relaxed mb-12 font-medium">
            At Delight Business Empowerment, we are driven by the belief that when people are empowered, communities thrive. Every initiative we launch is designed to create hope, unlock opportunities, and encourage people to take bold steps toward success.
          </p>
          <div className="flex flex-col items-center gap-6">
            <div className="flex flex-col items-center gap-3">
              <CheckCircle2 className="text-royal-gold w-8 h-8" />
              <span className="font-bold uppercase tracking-widest text-sm">Transparency</span>
            </div>
            <div className="flex flex-col items-center gap-3">
              <CheckCircle2 className="text-royal-gold w-8 h-8" />
              <span className="font-bold uppercase tracking-widest text-sm">Impact</span>
            </div>
            <div className="flex flex-col items-center gap-3">
              <CheckCircle2 className="text-royal-gold w-8 h-8" />
              <span className="font-bold uppercase tracking-widest text-sm">Excellence</span>
            </div>
          </div>
          <p className="mt-16 text-2xl font-display font-black text-royal-gold italic">
            "Delight Business Empowerment is where potential is discovered, dreams are supported, and success is nurtured."
          </p>
        </div>
      </section>

      {/* Gallery Modal */}
      <Modal
        isOpen={!!selectedWinner}
        onClose={() => setSelectedWinner(null)}
        title={`Miss ${selectedWinner?.name}`}
      >
        <div className="space-y-8">
          {/* Winner Info Header */}
          <div className="flex flex-wrap items-center gap-4 p-6 bg-royal-accent/30 rounded-[2rem] border border-amber-200">
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
              <Trophy className="w-4 h-4 text-royal-gold" />
              <span className="text-royal-dark font-black text-xs uppercase tracking-widest">{selectedWinner?.title}</span>
            </div>
            <div className="flex items-center gap-2 bg-royal-dark text-white px-4 py-2 rounded-full shadow-sm">
              <Sparkles className="w-4 h-4" />
              <span className="font-black text-xs uppercase tracking-widest">{selectedWinner?.prize} Grant</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {selectedWinner?.gallery?.map((img, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.1 }}
                className="aspect-[4/5] rounded-3xl overflow-hidden shadow-lg"
              >
                <img 
                  src={img} 
                  alt={`Gallery portrait of ${selectedWinner?.name ? `Miss ${selectedWinner.name}` : 'a Delight Business Empowerment winner'} ${idx + 1}`}
                  width={1200}
                  height={1500}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                  referrerPolicy="no-referrer"
                />
              </motion.div>
            ))}
          </div>
          <div className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100">
            <h4 className="text-xl font-display font-black text-royal-dark mb-4">About the Journey</h4>
            <p className="text-slate-600 leading-relaxed font-medium">
              {selectedWinner?.story || "This journey represents the dedication and hard work put into creating a lasting impact that inspires the community."}
            </p>
          </div>
        </div>
      </Modal>
      </div>
    </>
  );
};

export default Home;
